import { client } from "../clients";

/*
Simulation: Country List Filtering and Overrides (Frontend-only)

Who can use:
- Users in Okta groups: DEV_Okta_CDP_Developer or RC_Okta_CDP_Global_Admin

What it does:
- When allowed and query parameters are present, the original server response is:
  1) Filtered to include only countries specified in simCountries (names)
  2) Modified to override 'tags' for the filtered countries based on URL parameters
- If the user is not allowed or parameters are missing, the original server response is returned unchanged.

Supported URL parameters:
- simCountries: comma-separated country names (case-insensitive), e.g. "Poland,Australia,United%20Kingdom"
- simTag: comma-separated tags; overrides the 'tags' array for selected countries, e.g. "RC" or "RC,EH"

Examples:
- http://localhost:3000/campaigns?simTag=RC&simCountries=Poland,USA
- http://localhost:3000/campaigns?simTag=RC,EH&simCountries=Poland,USA

Response format:
- Unchanged structure: an object whose keys are country names and values contain:
  { admin: number, countryCode: string, tags: string[] }
*/

const fetchCountryList = async ({ ccdp_groups }: { ccdp_groups: string[] }) => {
  const response = await client("/get_countries", {
    method: "POST",
    data: { ccdp_groups },
  });
  const isAllowdToSimulate = ccdp_groups.includes("DEV_Okta_CDP_Developer") || ccdp_groups.includes("RC_Okta_CDP_Global_Admin");
  const params = new URLSearchParams(window.location.search);
  const simNamesParam = params.get("simCountries");
  const simTagParam = params.get("simTag");

  if (isAllowdToSimulate && simNamesParam) {
    const originalData = (response as any).data || {};
    const names = (simNamesParam || "")
      .split(",")
      .map((s) => decodeURIComponent(s).trim().toLowerCase())
      .filter(Boolean);
    const tagsOverride = (simTagParam || "")
      .split(",")
      .map((s) => decodeURIComponent(s).trim())
      .filter(Boolean);

    const filteredEntries = Object.entries(originalData)
      .filter(([name, details]: [string, any]) => {
        const nameLowerCase = name.toLowerCase();
        return names.includes(nameLowerCase);
      })
      .map(([name, details]: [string, any]) => {
        const next = { ...details };
        if (tagsOverride.length > 0) {
          next.tags = tagsOverride;
        }
        return [name, next] as [string, any];
      });

    const filteredData = Object.fromEntries(filteredEntries);
    return { ...response, data: filteredData };
  }
  return response;
};


export { fetchCountryList };
